import React, { useState, FC } from 'react'
import { Trans, t } from '@lingui/macro'
import classNames from 'classnames'
import { css } from '@emotion/react'

import {
  TopicElementModel,
  TopicElementModelAsEditor
} from 'front-end/src/graphql/private/PrivateSchemaTypes'

import IconLink from '../../../components/common/icon/IconLink'
import { IEmbedlyResult } from 'types/Embedly'
import { Link } from '@chakra-ui/react'
import { ButtonLight } from 'front-end/src/components'
import IconPencil from 'front-end/src/components/common/icon/IconPencil'

interface IEditLink {
  activateEditing?: () => void
}

export type LinkElementProps =
  | (TopicElementModelAsEditor & IEditLink)
  | (TopicElementModel & IEditLink)

const LinkElement: FC<LinkElementProps> = (props) => {
  const [modalShown, setModalShown] = useState(false)

  const closeModal = () => {
    setModalShown(false)
  }

  if (!props.data?.content) {
    // FE can sometimes obtain link with empty content which caused issues earlier.
    // This condition avoids these errors and renders nothing.
    console.warn('Looop: Unable to render LinkElement with empty content!')
    return null
  }

  let content = props.data?.content as IEmbedlyResult

  if (typeof content === 'string') {
    // hack
    // TODO migrate DB data to an object-storing JSON as plain string does not make sense in JSONB
    content = JSON.parse(content)
  }
  const hasImages = content.images && content.images.length

  const firstImage = hasImages ? (
    <div
      className="img"
      style={{ backgroundImage: `url('${content.images[0].url}')` }}
    />
  ) : null

  return (
    <div className="link-preview">
      <div>
        <div
          title={t`Click to read`}
          className={classNames('embedded-tile-wrapper clickable clearfix', {
            'no-image': !hasImages
          })}
          onClick={() => {
            if (content.content) {
              setModalShown(true)
            }
          }}
        >
          {firstImage}
          <h2>{content.title}</h2>
          <p>{content.description}</p>
          {content.provider_display ? (
            <p className="info">
              <Trans>Source</Trans>: {content.provider_display}
            </p>
          ) : (
            <p
              className="info"
              css={css`
                display: flex;
                align-items: center;
              `}
            >
              <IconLink
                size="medium"
                css={css`
                  margin-right: 20px;
                `}
              />
              <Trans>Link</Trans>: {content.url}
            </p>
          )}

          {content.content ? (
            <div className="embedded-tile-instruction">
              <span className="icon-size-fullscreen" />{' '}
              <Trans>Open page reader</Trans>
            </div>
          ) : (
            <a
              className="embedded-tile-instruction"
              rel="noopener noreferrer"
              target="_blank"
              href={content.url}
            >
              <span className="icon-size-fullscreen" />{' '}
              <Trans>Open in new tab</Trans>
            </a>
          )}
        </div>

        {modalShown && (
          <div className="embedded-page-modal-wrapper modal-wrapper">
            <div className="modal-mask" onClick={closeModal} />
            <div
              className="embedded-page-modal-body modal-body"
              ng-click="preventDefault"
            >
              <div className="modal-content">
                <div className="embedded-title">{content.title}</div>
                <div
                  className="embedded-content"
                  dangerouslySetInnerHTML={{ __html: content.content }}
                />
              </div>

              <div className="modal-header">
                <a
                  className="clickable btn btn-default close-modal"
                  onClick={closeModal}
                >
                  <span className="icon-size-actual" />
                </a>
                <a
                  className="clickable btn btn-default"
                  href={content.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="icon icon-share-alt" />
                  <span className="btn-label">
                    {' '}
                    <Trans>Open in browser</Trans>
                  </span>
                </a>

                <h3>
                  {content.favicon_url && (
                    <img className="favicon" src={content.favicon_url} />
                  )}
                  {content.provider_display}
                </h3>
              </div>
            </div>
          </div>
        )}
      </div>
      {props.activateEditing && (
        <Link className="link-clear" onClick={props.activateEditing}>
          <ButtonLight leftIcon={<IconPencil />}>
            <Trans>Edit</Trans>
          </ButtonLight>
        </Link>
      )}
    </div>
  )
}

export default LinkElement
